import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  makeStyles,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Chip,
  Box,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import SettingsIcon from "@material-ui/icons/Settings";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import EditIcon from "@material-ui/icons/Edit";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useHRSettings } from "../../../../../providers/HRSettingsProvider";
import { useAuth } from "../../../../../providers/AuthContext";
import { firestore } from "../../../../../firebase/firebase";

const useSharedStyles = makeStyles((theme) => ({
  listItem: {
    paddingLeft: 24,
    flexShrink: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  listItemWithActions: {
    paddingLeft: 24,
    paddingRight: 80,
    flexShrink: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  addButton: {
    paddingLeft: 24,
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  dragHandle: {
    cursor: "grab",
    marginRight: 4,
    color: theme.palette.text.secondary,
  },
  listItemText: {
    overflow: "hidden",
    minWidth: 0,
  },
  marqueeWrap: {
    overflow: "hidden",
    display: "block",
    width: "100%",
  },
  marqueeText: {
    display: "inline-block",
    whiteSpace: "nowrap",
  },
}));

// ============================================================
// Text that scrolls on hover if it overflows its container
// ============================================================
function MarqueeText({ text }) {
  const classes = useSharedStyles();
  const textRef = useRef(null);
  const wrapRef = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [scrollDuration, setScrollDuration] = useState(3);
  const delayTimer = useRef(null);

  const checkOverflow = useCallback(() => {
    if (textRef.current && wrapRef.current && text.length >= 20) {
      const isOverflow = textRef.current.scrollWidth > wrapRef.current.clientWidth;
      setOverflows(isOverflow);
      if (isOverflow) {
        const overflow = textRef.current.scrollWidth - wrapRef.current.clientWidth;
        setScrollDuration(Math.max(4, overflow / 15));
      }
    } else {
      setOverflows(false);
    }
  }, [text]);

  useEffect(() => {
    const timer = setTimeout(checkOverflow, 350);
    const ro = wrapRef.current && window.ResizeObserver
      ? new ResizeObserver(checkOverflow) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    return () => { clearTimeout(timer); if (ro) ro.disconnect(); };
  }, [checkOverflow]);

  useEffect(() => {
    if (hovered && overflows) {
      delayTimer.current = setTimeout(() => setAnimating(true), 1000);
    } else {
      clearTimeout(delayTimer.current);
      setAnimating(false);
    }
    return () => clearTimeout(delayTimer.current);
  }, [hovered, overflows]);

  const animStyle = animating
    ? { animation: `hrMarqueeScroll ${scrollDuration}s linear infinite` }
    : {};

  return (
    <span
      ref={wrapRef}
      className={classes.marqueeWrap}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span ref={textRef} className={classes.marqueeText} style={animStyle}>
        {text}
      </span>
      {overflows && (
        <style>{`
          @keyframes hrMarqueeScroll {
            0% { transform: translateX(0); opacity: 1; }
            80% { transform: translateX(-100%); opacity: 1; }
            85% { transform: translateX(-100%); opacity: 0; }
            86% { transform: translateX(0); opacity: 0; }
            90% { transform: translateX(0); opacity: 1; }
            100% { transform: translateX(0); opacity: 1; }
          }
        `}</style>
      )}
    </span>
  );
}

// ============================================================
// Sortable list item wrapper
// ============================================================
function SortableListItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : "auto",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ dragListeners: listeners })}
    </div>
  );
}

// ============================================================
// Shared dialog hook for add/edit/delete operations
// ============================================================
function useHRDialogs() {
  const { subtypes: providerSubtypes, addType, deleteType, addSubtype, addSubtypeWithSubfolders, deleteSubtype, updateType, updateSubtype } = useHRSettings();

  const [addTypeOpen, setAddTypeOpen] = useState(false);
  const [addSubtypeOpen, setAddSubtypeOpen] = useState(false);
  const [editTypeOpen, setEditTypeOpen] = useState(false);
  const [editSubtypeOpen, setEditSubtypeOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [newName, setNewName] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteBlockedReason, setDeleteBlockedReason] = useState("");
  const [parentForAdd, setParentForAdd] = useState(null);
  const [parentFolderForAdd, setParentFolderForAdd] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [editName, setEditName] = useState("");
  const [editHidden, setEditHidden] = useState(false);
  const [editHasSubfolders, setEditHasSubfolders] = useState(false);
  const [editHasParentFolder, setEditHasParentFolder] = useState(false);
  const [editParentFolder, setEditParentFolder] = useState("");

  // Subfolder creation state (for Add Subtype dialog)
  const [enableSubfolders, setEnableSubfolders] = useState(false);
  const [newSubfolderNames, setNewSubfolderNames] = useState([]);
  const [subfolderInput, setSubfolderInput] = useState("");

  // Edit warning state
  const [editSubfoldersWarning, setEditSubfoldersWarning] = useState("");

  const resetDialogState = () => {
    setNewName("");
    setEditItem(null);
    setDeleteItem(null);
    setDeleteBlockedReason("");
    setParentForAdd(null);
    setParentFolderForAdd(null);
    setSaving(false);
    setError("");
    setEditName("");
    setEditHidden(false);
    setEditHasSubfolders(false);
    setEditHasParentFolder(false);
    setEditParentFolder("");
    setEnableSubfolders(false);
    setNewSubfolderNames([]);
    setSubfolderInput("");
    setEditSubfoldersWarning("");
  };

  const handleAddType = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    setError("");
    try {
      await addType(newName.trim());
      setAddTypeOpen(false);
      resetDialogState();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const handleAddSubtype = async () => {
    if (!newName.trim() || !parentForAdd) return;
    setSaving(true);
    setError("");
    try {
      if (enableSubfolders && newSubfolderNames.length > 0) {
        await addSubtypeWithSubfolders(parentForAdd, newName.trim(), newSubfolderNames, parentFolderForAdd || undefined);
      } else {
        await addSubtype(parentForAdd, newName.trim(), parentFolderForAdd || undefined);
      }
      setAddSubtypeOpen(false);
      resetDialogState();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const openEditType = (type) => {
    setEditItem(type);
    setEditName(type.name);
    setError("");
    setEditTypeOpen(true);
  };

  const handleEditType = async () => {
    if (!editName.trim() || !editItem) return;
    setSaving(true);
    setError("");
    try {
      await updateType(editItem.value, { name: editName.trim() });
      setEditTypeOpen(false);
      resetDialogState();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const openEditSubtype = (subtype) => {
    setEditItem(subtype);
    setEditName(subtype.name);
    setEditHidden(subtype.hidden || false);
    setEditHasSubfolders(subtype.hasSubfolders || false);
    setEditHasParentFolder(subtype.hasParentFolder || false);
    setEditParentFolder(subtype.parentFolder || "");
    setError("");
    setEditSubtypeOpen(true);
  };

  const handleEditSubfoldersToggle = async (checked) => {
    setEditHasSubfolders(checked);
    setEditSubfoldersWarning("");
    if (checked && editItem) {
      try {
        const docsSnapshot = await firestore
          .collection("humanResources")
          .where("hrType", "==", editItem.parentValue)
          .where("docType", "==", editItem.value)
          .get();
        if (!docsSnapshot.empty) {
          setEditSubfoldersWarning(
            `This folder contains ${docsSnapshot.size} document${docsSnapshot.size !== 1 ? "s" : ""}. You'll need to move them into subfolders using the table's Select to Move feature.`
          );
        }
      } catch (e) {
        console.warn("Error checking for documents:", e);
      }
    }
  };

  const handleEditSubtype = async () => {
    if (!editName.trim() || !editItem) return;
    setSaving(true);
    setError("");
    try {
      await updateSubtype(editItem.parentValue, editItem.value, {
        name: editName.trim(),
        hidden: editHidden,
        hasSubfolders: editHasSubfolders,
        hasParentFolder: editHasParentFolder,
        parentFolder: editHasParentFolder ? editParentFolder : "",
      });
      setEditSubtypeOpen(false);
      resetDialogState();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const openDeleteConfirm = async (item, itemType) => {
    setDeleteItem({ ...item, itemType });
    setError("");
    let blocked = "";
    if (itemType === "type") {
      if ((providerSubtypes || []).some((s) => s.parentValue === item.value)) {
        blocked = "Cannot delete this folder because it has folders inside it.";
      }
    } else {
      // Check for child folders
      if ((providerSubtypes || []).some((s) => s.parentFolder === item.value && s.parentValue === item.parentValue)) {
        blocked = "Cannot delete this folder because it has child folders.";
      }
      // Check for documents (forms) in this folder
      if (!blocked) {
        try {
          const docsSnapshot = await firestore
            .collection("humanResources")
            .where("hrType", "==", item.parentValue)
            .where("docType", "==", item.value)
            .limit(1)
            .get();
          if (!docsSnapshot.empty) {
            blocked = "Cannot delete this folder because it has documents.";
          }
        } catch (e) {
          console.warn("Error checking for documents:", e);
        }
      }
    }
    setDeleteBlockedReason(blocked);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setSaving(true);
    setError("");
    try {
      if (deleteItem.itemType === "type") {
        await deleteType(deleteItem.value);
      } else {
        await deleteSubtype(deleteItem.parentValue, deleteItem.value);
      }
      setDeleteConfirmOpen(false);
      resetDialogState();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const openAddType = () => {
    setNewName("");
    setError("");
    setAddTypeOpen(true);
  };

  const openAddSubtype = (parentValue, parentFolderValue) => {
    setParentForAdd(parentValue);
    setParentFolderForAdd(parentFolderValue || null);
    setNewName("");
    setError("");
    setAddSubtypeOpen(true);
  };

  return {
    openAddType, openAddSubtype, openEditType, openEditSubtype, openDeleteConfirm,
    addTypeOpen, setAddTypeOpen,
    addSubtypeOpen, setAddSubtypeOpen,
    editTypeOpen, setEditTypeOpen,
    editSubtypeOpen, setEditSubtypeOpen,
    deleteConfirmOpen, setDeleteConfirmOpen,
    newName, setNewName,
    editItem, deleteItem, deleteBlockedReason,
    saving, error,
    editName, setEditName,
    editHidden, setEditHidden,
    editHasSubfolders, setEditHasSubfolders,
    editHasParentFolder, setEditHasParentFolder,
    editParentFolder, setEditParentFolder,
    handleAddType, handleAddSubtype,
    handleEditType, handleEditSubtype, handleEditSubfoldersToggle,
    handleDelete, resetDialogState,
    enableSubfolders, setEnableSubfolders,
    newSubfolderNames, setNewSubfolderNames,
    subfolderInput, setSubfolderInput,
    editSubfoldersWarning,
    parentFolderForAdd,
  };
}

// ============================================================
// Shared Dialogs renderer
// ============================================================
function HRDialogs({ dialogs, subtypes }) {
  const classes = useSharedStyles();
  const {
    addTypeOpen, setAddTypeOpen,
    addSubtypeOpen, setAddSubtypeOpen,
    editTypeOpen, setEditTypeOpen,
    editSubtypeOpen, setEditSubtypeOpen,
    deleteConfirmOpen, setDeleteConfirmOpen,
    newName, setNewName,
    editItem, deleteItem, deleteBlockedReason,
    saving, error,
    editName, setEditName,
    editHidden, setEditHidden,
    editHasSubfolders, setEditHasSubfolders,
    editHasParentFolder, setEditHasParentFolder,
    editParentFolder, setEditParentFolder,
    handleAddType, handleAddSubtype,
    handleEditType, handleEditSubtype, handleEditSubfoldersToggle,
    handleDelete, resetDialogState,
    enableSubfolders, setEnableSubfolders,
    newSubfolderNames, setNewSubfolderNames,
    subfolderInput, setSubfolderInput,
    editSubfoldersWarning,
    parentFolderForAdd,
  } = dialogs;

  const folderOptions = editItem
    ? (subtypes || []).filter(
        (s) =>
          s.parentValue === editItem.parentValue &&
          s.hasSubfolders &&
          s.value !== editItem.value
      )
    : [];

  return (
    <>
      {/* Add Type Dialog */}
      <Dialog open={addTypeOpen} onClose={() => { setAddTypeOpen(false); resetDialogState(); }} maxWidth="xs" fullWidth>
        <DialogTitle>Add Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Folder Name" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className={classes.formField}
            onKeyDown={(e) => e.key === "Enter" && handleAddType()}
          />
          {error && <Typography className={classes.errorText}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddTypeOpen(false); resetDialogState(); }}>Cancel</Button>
          <Button onClick={handleAddType} color="primary" disabled={saving || !newName.trim()}>
            {saving ? <CircularProgress size={20} /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Subtype Dialog */}
      <Dialog open={addSubtypeOpen} onClose={() => { setAddSubtypeOpen(false); resetDialogState(); }} maxWidth="xs" fullWidth>
        <DialogTitle>Add Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Folder Name" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className={classes.formField}
            onKeyDown={(e) => !enableSubfolders && e.key === "Enter" && handleAddSubtype()}
          />
          {/* Only show "Has Subfolders" when NOT inside an existing parent folder */}
          {!parentFolderForAdd && (
            <>
              <FormControlLabel
                control={<Switch checked={enableSubfolders} onChange={(e) => setEnableSubfolders(e.target.checked)} />}
                label="Has Subfolders"
                className={classes.formField}
              />
              {enableSubfolders && (
                <Box>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <TextField
                      fullWidth
                      label="Subfolder Name"
                      value={subfolderInput}
                      onChange={(e) => setSubfolderInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && subfolderInput.trim()) {
                          e.preventDefault();
                          if (!newSubfolderNames.includes(subfolderInput.trim())) {
                            setNewSubfolderNames([...newSubfolderNames, subfolderInput.trim()]);
                          }
                          setSubfolderInput("");
                        }
                      }}
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!subfolderInput.trim()}
                      onClick={() => {
                        if (subfolderInput.trim() && !newSubfolderNames.includes(subfolderInput.trim())) {
                          setNewSubfolderNames([...newSubfolderNames, subfolderInput.trim()]);
                        }
                        setSubfolderInput("");
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginTop: 8 }}>
                    {newSubfolderNames.map((name) => (
                      <Chip
                        key={name}
                        label={name}
                        onDelete={() => setNewSubfolderNames(newSubfolderNames.filter((n) => n !== name))}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
          {error && <Typography className={classes.errorText}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddSubtypeOpen(false); resetDialogState(); }}>Cancel</Button>
          <Button
            onClick={handleAddSubtype}
            color="primary"
            disabled={saving || !newName.trim() || (enableSubfolders && newSubfolderNames.length === 0)}
          >
            {saving ? <CircularProgress size={20} /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Type Dialog */}
      <Dialog open={editTypeOpen} onClose={() => { setEditTypeOpen(false); resetDialogState(); }} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Folder Name" value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className={classes.formField}
            onKeyDown={(e) => e.key === "Enter" && handleEditType()}
          />
          {error && <Typography className={classes.errorText}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditTypeOpen(false); resetDialogState(); }}>Cancel</Button>
          <Button onClick={handleEditType} color="primary" disabled={saving || !editName.trim()}>
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Subtype Dialog */}
      <Dialog open={editSubtypeOpen} onClose={() => { setEditSubtypeOpen(false); resetDialogState(); }} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Folder Name" value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className={classes.formField}
          />
          <FormControlLabel
            control={<Switch checked={editHidden} onChange={(e) => setEditHidden(e.target.checked)} />}
            label={editHidden ? "Hidden" : "Visible"}
            className={classes.formField}
          />
          <br />
          <FormControlLabel
            control={<Switch checked={editHasSubfolders} onChange={(e) => handleEditSubfoldersToggle(e.target.checked)} disabled={editHasParentFolder} />}
            label="Has Subfolders"
            className={classes.formField}
          />
          {editSubfoldersWarning && (
            <Typography variant="body2" style={{ color: "#f57c00", marginBottom: 8 }}>
              {editSubfoldersWarning}
            </Typography>
          )}
          <br />
          <FormControlLabel
            control={<Switch checked={editHasParentFolder} onChange={(e) => setEditHasParentFolder(e.target.checked)} disabled={editHasSubfolders} />}
            label="Has Parent Folder"
            className={classes.formField}
          />
          {editHasParentFolder && (
            <FormControl fullWidth className={classes.formField}>
              <InputLabel>Parent Folder</InputLabel>
              <Select value={editParentFolder} onChange={(e) => setEditParentFolder(e.target.value)}>
                {folderOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {error && <Typography className={classes.errorText}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditSubtypeOpen(false); resetDialogState(); }}>Cancel</Button>
          <Button onClick={handleEditSubtype} color="primary" disabled={saving || !editName.trim()}>
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => { setDeleteConfirmOpen(false); resetDialogState(); }} maxWidth="xs" fullWidth>
        <DialogTitle>{deleteBlockedReason ? "Cannot Delete" : "Confirm Delete"}</DialogTitle>
        <DialogContent>
          {deleteBlockedReason ? (
            <Typography className={classes.errorText}>{deleteBlockedReason}</Typography>
          ) : (
            <Typography>
              Are you sure you want to delete &quot;{deleteItem?.name}&quot;?
            </Typography>
          )}
          {error && <Typography className={classes.errorText}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirmOpen(false); resetDialogState(); }}>
            {deleteBlockedReason ? "Close" : "Cancel"}
          </Button>
          {!deleteBlockedReason && (
            <Button onClick={handleDelete} color="secondary" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : "Delete"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

// ============================================================
// Main HR Management Drawer (level 1 - types list)
// ============================================================
export const HRManagementDrawer = (props) => {
  const classes = useSharedStyles();
  const { types, subtypes, reorderTypes } = useHRSettings();
  const { roles } = useAuth();
  const canEdit = roles.includes("Edit HR Folder Settings");
  const [editing, setEditing] = useState(false);
  const dialogs = useHRDialogs();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const typeValues = (types || []).map((t) => t.value);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = typeValues.indexOf(active.id);
      const newIndex = typeValues.indexOf(over.id);
      const newOrder = arrayMove(typeValues, oldIndex, newIndex);
      reorderTypes(newOrder);
    }
  };

  const typeListContent = (types || []).map((type) => {
    const handleTypeClick = () => {
      if (props.openSubSubDrawer) {
        props.openSubSubDrawer(<HRSubtypeDrawer typeValue={type.value} />);
      } else {
        props.onClick && props.onClick();
      }
    };

    if (!editing) {
      return (
        <ListItem
          className={classes.listItem}
          onClick={handleTypeClick}
          button
          key={type.value}
        >
          <ListItemText className={classes.listItemText} primary={<MarqueeText text={type.name} />} />
        </ListItem>
      );
    }

    return (
      <SortableListItem key={type.value} id={type.value}>
        {({ dragListeners }) => (
          <ListItem
            className={classes.listItemWithActions}
            onClick={handleTypeClick}
            button
          >
            <span {...dragListeners} className={classes.dragHandle}>
              <DragHandleIcon fontSize="small" />
            </span>
            <ListItemText className={classes.listItemText} primary={<MarqueeText text={type.name} />} />
            <ListItemSecondaryAction>
              <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); dialogs.openEditType(type); }}>
                <SettingsIcon fontSize="small" />
              </IconButton>
              <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); dialogs.openDeleteConfirm(type, "type"); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        )}
      </SortableListItem>
    );
  });

  return (
    <>
      <List>
        {/* Accessibility link (matches createItems logic) */}
        {(subtypes || []).some((s) => s.name === "Accessibility") && (
          <ListItem
            className={classes.listItem}
            onClick={() => props.onClick && props.onClick()}
            component={Link}
            to={(() => {
              const acc = subtypes.find((s) => s.name === "Accessibility");
              return acc?.override
                ? acc.overrideValue
                : `/dashboard/hr/${acc?.parentValue}/${acc?.value}`;
            })()}
            button
          >
            <ListItemText primary="Accessibility" />
          </ListItem>
        )}

        {/* Types list with drag-and-drop when editing */}
        {editing ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={typeValues} strategy={verticalListSortingStrategy}>
              {typeListContent}
            </SortableContext>
          </DndContext>
        ) : (
          typeListContent
        )}

        {canEdit && (
          <>
            <Divider />
            <ListItem className={classes.addButton} button onClick={dialogs.openAddType}>
              <AddIcon fontSize="small" />&nbsp;
              <ListItemText primary="Add Folder" />
            </ListItem>
            <ListItem className={classes.addButton} button onClick={() => setEditing(!editing)}>
              <EditIcon fontSize="small" />&nbsp;
              <ListItemText primary={editing ? "Done" : "Edit"} />
            </ListItem>
          </>
        )}
      </List>
      <HRDialogs dialogs={dialogs} subtypes={subtypes} />
    </>
  );
};

// ============================================================
// Subtype Drawer (works at any nesting level)
// Self-contained: uses its own hooks for fresh data
// Receives onClick and openSubSubDrawer via React.cloneElement
// ============================================================
const HRSubtypeDrawer = (props) => {
  const { typeValue, parentFolderValue } = props;
  const classes = useSharedStyles();
  const { types, subtypes, reorderSubtypes } = useHRSettings();
  const { roles } = useAuth();
  const canEdit = roles.includes("Edit HR Folder Settings");
  const [editing, setEditing] = useState(false);
  const dialogs = useHRDialogs();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Get subtypes at this nesting level
  const levelSubtypes = (subtypes || [])
    .filter((s) => {
      if (parentFolderValue) {
        return s.parentValue === typeValue && s.parentFolder === parentFolderValue;
      }
      return s.parentValue === typeValue && !s.hasParentFolder;
    })
    .sort((a, b) => a.order - b.order);

  const subtypeValues = levelSubtypes.map((s) => s.value);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = subtypeValues.indexOf(active.id);
      const newIndex = subtypeValues.indexOf(over.id);
      const newOrder = arrayMove(subtypeValues, oldIndex, newIndex);
      reorderSubtypes(typeValue, parentFolderValue || null, newOrder);
    }
  };

  const listItems = levelSubtypes.map((sub) => {
    if (sub.hidden && !canEdit) return null;

    const hasChildren = sub.hasSubfolders;

    const handleClick = () => {
      if (hasChildren && props.openSubSubDrawer) {
        props.openSubSubDrawer(
          <HRSubtypeDrawer typeValue={typeValue} parentFolderValue={sub.value} />
        );
      } else if (!hasChildren) {
        props.onClick && props.onClick();
      }
    };

    const linkTo = !hasChildren
      ? sub.override
        ? sub.overrideValue
        : `/dashboard/hr/${typeValue}/${sub.value}`
      : undefined;

    if (!editing) {
      return (
        <ListItem
          className={classes.listItem}
          onClick={handleClick}
          component={!hasChildren ? Link : "div"}
          to={linkTo}
          button
          key={sub.value}
        >
          <ListItemText className={classes.listItemText} primary={<MarqueeText text={sub.name} />} />
        </ListItem>
      );
    }

    return (
      <SortableListItem key={sub.value} id={sub.value}>
        {({ dragListeners }) => (
          <ListItem
            className={classes.listItemWithActions}
            onClick={handleClick}
            component={!hasChildren ? Link : "div"}
            to={linkTo}
            button
          >
            <span {...dragListeners} className={classes.dragHandle}>
              <DragHandleIcon fontSize="small" />
            </span>
            <ListItemText className={classes.listItemText} primary={<MarqueeText text={sub.name} />} />
            <ListItemSecondaryAction>
              <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); dialogs.openEditSubtype(sub); }}>
                <SettingsIcon fontSize="small" />
              </IconButton>
              <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); dialogs.openDeleteConfirm(sub, "subtype"); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        )}
      </SortableListItem>
    );
  });

  return (
    <>
      <List>
        {editing ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={subtypeValues} strategy={verticalListSortingStrategy}>
              {listItems}
            </SortableContext>
          </DndContext>
        ) : (
          listItems
        )}

        {canEdit && (
          <>
            <Divider />
            <ListItem className={classes.addButton} button onClick={() => dialogs.openAddSubtype(typeValue, parentFolderValue)}>
              <AddIcon fontSize="small" />&nbsp;
              <ListItemText primary="Add Folder" />
            </ListItem>
            <ListItem className={classes.addButton} button onClick={() => setEditing(!editing)}>
              <EditIcon fontSize="small" />&nbsp;
              <ListItemText primary={editing ? "Done" : "Edit"} />
            </ListItem>
          </>
        )}
      </List>
      <HRDialogs dialogs={dialogs} subtypes={subtypes} />
    </>
  );
};
